import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { TwoFactorSetup, TwoFactorVerify } from "@/components/auth/TwoFactor";
import { EmailCodeVerify, MethodChooser } from "@/components/auth/EmailCode";
import { HeroTicker } from "@/components/HeroTicker";

export default function Login() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(null);
  const finish = (u) => { setSession(u); toast.success("Signed in"); nav("/dashboard"); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.setup_required) { setStep({ kind: data.can_use_email ? "choose" : "setup", token: data.mfa_token, email: data.email, canEmail: data.can_use_email }); return; }
      if (data.mfa_required) { setStep({ kind: "verify", token: data.mfa_token, email: data.email, canEmail: data.can_use_email }); return; }
      if (data.email_code_required) { setStep({ kind: "email", token: data.mfa_token, email: data.email }); return; }
      finish(data.user);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 text-slate-100 p-12 relative overflow-hidden login-hero" data-testid="login-hero">
        <div className="login-hero-glow" aria-hidden="true" />
        <div className="login-hero-lanes" aria-hidden="true" data-testid="login-hero-lanes"><span /><span /><span /><span /></div>
        <div className="login-hero-shield" aria-hidden="true">
          <svg viewBox="0 0 200 240" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 8 L184 40 V118 C184 172 148 212 100 232 C52 212 16 172 16 118 V40 Z" stroke="currentColor" strokeWidth="2" />
            <path d="M100 40 L156 62 V116 C156 154 130 184 100 200 C70 184 44 154 44 116 V62 Z" stroke="currentColor" strokeWidth="1.5" opacity=".55" />
            <path d="M70 122 L92 144 L134 96" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" opacity=".9" />
          </svg>
        </div>
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-12 w-12 rounded-md bg-white flex items-center justify-center overflow-hidden">
            <img src="/safer-highways-logo.png" alt="Safer Highways Inc" className="h-11 w-11 object-contain" data-testid="login-logo" />
          </div>
          <div>
            <div className="tag-mono text-blue-300">SAFER HIGHWAYS INC</div>
            <div className="text-lg font-semibold">Compliance Portal</div>
          </div>
        </div>
        <div className="relative z-10">
          <div className="tag-mono text-blue-300 mb-4">DOT / FMCSA · PART 382</div>
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight font-heading">
            Your One-Stop Shop to Full Compliance
          </h1>
          <HeroTicker />
          <p className="mt-6 text-slate-300 max-w-md">
            Drug &amp; alcohol consortium, driver files, PSP/MVR/background reports, IRP · 2290 · IFTA filings and insurance packets — tracked, reminded and approved in one place.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Consortium", "Driver qualification", "Background & MVR", "Fleet filings", "Expiration alerts"].map((t) => (
              <span key={t} className="tag-mono text-[11px] px-3 py-1.5 rounded-full border border-blue-400/30 bg-blue-500/10 text-blue-200">{t}</span>
            ))}
          </div>
        </div>
          <div className="relative z-10 space-y-3">
            <div className="rounded-lg border border-blue-400/30 bg-blue-500/10 p-4 text-sm text-slate-200" data-testid="login-sms-program">
              <div className="tag-mono text-[11px] text-blue-300 mb-1">TEXT-MESSAGE PROGRAM</div>
              <p>Safer Highways Inc sends DOT compliance texts (document requests, reminders, e-sign links) only to drivers and carrier contacts who opt in themselves. Up to 4 msgs/month. Msg &amp; data rates may apply. Reply STOP to cancel, HELP for help.</p>
              <p className="mt-2"><Link to="/text-alerts" className="underline text-blue-200" data-testid="login-text-alerts-link">Opt in to compliance text alerts →</Link></p>
            </div>
            <div className="tag-mono text-slate-400">© {new Date().getFullYear()} Safer Highways Inc · <Link to="/privacy" className="underline">Privacy</Link> · <Link to="/terms" className="underline">Terms</Link> · <Link to="/sms-terms" className="underline">SMS Terms</Link></div>
          </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md p-8">
          <div className="mb-6">
            <div className="tag-mono text-blue-700 mb-2">SIGN IN</div>
            <h2 className="text-2xl font-bold font-heading text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-600 mt-1">
              Access your account. New customer?{" "}
              <span className="text-slate-800 font-medium">Ask Britt for an invite.</span>
            </p>
          </div>
          {step ? (
            <Card className="p-5" data-testid="login-twofa-step">
              {step.kind === "choose" && <MethodChooser email={step.email} onPick={(m) => setStep({ ...step, kind: m === "email" ? "email" : "setup" })} />}
              {step.kind === "email" && <EmailCodeVerify mfaToken={step.token} email={step.email} onComplete={finish} onBack={() => setStep(null)} onUseApp={step.canEmail ? () => setStep({ ...step, kind: "setup" }) : undefined} />}
              {step.kind === "setup" && <TwoFactorSetup mfaToken={step.token} email={step.email} onComplete={finish} onUseEmail={step.canEmail ? () => setStep({ ...step, kind: "email" }) : undefined} />}
              {step.kind === "verify" && <TwoFactorVerify mfaToken={step.token} email={step.email} onComplete={finish} onBack={() => setStep(null)} onUseEmail={step.canEmail ? () => setStep({ ...step, kind: "email" }) : undefined} />}
            </Card>
          ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-700 hover:bg-blue-800"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          )}
          <div className="mt-6 text-xs text-slate-500 flex justify-between">
            <Link to="/forgot-password" className="hover:text-slate-800" data-testid="forgot-password-link">Forgot password?</Link>
            <Link to="/accept-invite" className="hover:text-slate-800">Have an invite token? Accept it →</Link>
          </div>
          <div className="mt-3 text-[11px] text-slate-400 flex gap-3 justify-center">
            <Link to="/privacy" className="hover:text-slate-700" data-testid="privacy-link">Privacy Policy</Link><span>·</span><Link to="/terms" className="hover:text-slate-700" data-testid="terms-link">Terms</Link><span>·</span><Link to="/sms-terms" className="hover:text-slate-700" data-testid="sms-terms-link">SMS Terms</Link><span>·</span><Link to="/text-alerts" className="hover:text-slate-700" data-testid="text-alerts-link">Text alerts</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
